import type {
  RastersToPngWorkerRequest,
  RastersToPngWorkerResponse,
} from "./rastersToPng.worker";
import { prepareRasterTransfer, rastersToPngBytes } from "./rastersToPngCore";

/** Matches COG detail tile decode concurrency. */
const WORKER_POOL_SIZE = 3;

interface PendingEncodeJob {
  height: number;
  reject: (error: Error) => void;
  resolve: (pngBytes: ArrayBuffer) => void;
  rasters: ArrayLike<number>;
  samplesPerPixel: number;
  width: number;
}

interface WorkerSlot {
  busy: boolean;
  worker: Worker;
}

let workerPool: RastersToPngWorkerPool | null = null;

const canUseWorkers = (): boolean => typeof Worker !== "undefined";

class RastersToPngWorkerPool {
  private activeJobs = new Map<Worker, PendingEncodeJob>();

  private jobQueue: PendingEncodeJob[] = [];

  private slots: WorkerSlot[] = [];

  constructor(poolSize: number) {
    for (let index = 0; index < poolSize; index += 1) {
      this.slots.push(this.createSlot());
    }
  }

  encode(
    rasters: ArrayLike<number>,
    width: number,
    height: number,
    samplesPerPixel: number,
  ): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      this.jobQueue.push({
        height,
        reject,
        resolve,
        rasters,
        samplesPerPixel,
        width,
      });
      this.dispatchJobs();
    });
  }

  private createSlot(): WorkerSlot {
    const worker = new Worker(
      new URL("./rastersToPng.worker.ts", import.meta.url),
      { type: "module" },
    );

    const slot: WorkerSlot = { busy: false, worker };

    worker.addEventListener(
      "message",
      (event: MessageEvent<RastersToPngWorkerResponse>) => {
        const response = event.data;
        slot.busy = false;

        const job = this.activeJobs.get(worker);
        this.activeJobs.delete(worker);
        if (job == null) {
          this.dispatchJobs();
          return;
        }

        if (response.type === "error") {
          job.reject(new Error(response.message));
        } else {
          job.resolve(response.pngBytes);
        }

        this.dispatchJobs();
      },
    );

    worker.addEventListener("error", (event: ErrorEvent) => {
      slot.busy = false;
      const job = this.activeJobs.get(worker);
      this.activeJobs.delete(worker);
      if (job != null) {
        job.reject(
          new Error(
            event.message || "Raster-to-PNG worker failed unexpectedly",
          ),
        );
      }
      this.replaceSlot(slot);
      this.dispatchJobs();
    });

    return slot;
  }

  private dispatchJobs(): void {
    for (const slot of this.slots) {
      if (slot.busy || this.jobQueue.length === 0) {
        continue;
      }

      const job = this.jobQueue.shift();
      if (job == null) {
        return;
      }

      slot.busy = true;
      this.activeJobs.set(slot.worker, job);

      const transfer = prepareRasterTransfer(job.rasters);
      const request: RastersToPngWorkerRequest = {
        height: job.height,
        rasters: transfer,
        samplesPerPixel: job.samplesPerPixel,
        type: "encode",
        width: job.width,
      };

      slot.worker.postMessage(request, [transfer.rastersBuffer]);
    }
  }

  private replaceSlot(failedSlot: WorkerSlot): void {
    failedSlot.worker.terminate();
    const index = this.slots.indexOf(failedSlot);
    if (index === -1) {
      return;
    }
    this.slots[index] = this.createSlot();
  }
}

const getWorkerPool = (): RastersToPngWorkerPool => {
  if (workerPool == null) {
    workerPool = new RastersToPngWorkerPool(WORKER_POOL_SIZE);
  }
  return workerPool;
};

/** Offloads pixel packing + PNG encode to a worker pool; falls back on main thread. */
export const rastersToPngBytesInWorker = async (
  rasters: ArrayLike<number>,
  width: number,
  height: number,
  samplesPerPixel: number,
): Promise<ArrayBuffer> => {
  if (!canUseWorkers()) {
    return rastersToPngBytes(rasters, width, height, samplesPerPixel);
  }

  return getWorkerPool().encode(rasters, width, height, samplesPerPixel);
};
